import { CognitoUserPool, CognitoUser, AuthenticationDetails, } from 'amazon-cognito-identity-js';
import { promisify } from 'es6-promisify';
import { CognitoAuth } from './auth';
import { NewUserNotAvailableError, MissingCognitoConfigError, UserDoesNotExistError } from './errors';
import { MemoryStorage } from './storage';
export { CognitoUserSession } from 'amazon-cognito-identity-js';
export * from './errors';
/**
 * Promisifies all `functions` of the `obj`.
 * @returns The object `obj` parameter with all `functions` promisified.
 */
export function promisifyAll(obj, functions) {
    if (!obj || !Array.isArray(functions))
        return obj;
    for (const f of functions) {
        if (typeof obj[f] !== 'function')
            continue;
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
        obj[f] = promisify(obj[f]);
    }
    return obj;
}
// Currently both amazon-cognito-identity-js and @aws-amplify/auth are used here, latter for sso only.
// amazon-cognito-identity-js is lighter and doesn't provide nearly all functionality of @aws-amplify/auth,
// it's is also used internally by @aws-amplify/auth.
// Consider dropping amazon-cognito-identity-js in favor of amplify (bundle size needs optimization first though).
export class MyGranoAuth {
    constructor(settings) {
        this.tokenStorage = new MemoryStorage();
        this.isSsoAuthInitializing = Promise.resolve(false);
        this.authUrl = settings.authUrl;
        this.locale = settings.locale || 'fi';
        this.serviceCognitoConfig = settings.cognitoConfig;
        const searchParams = this.getSearchParams();
        const newrelic = window.newrelic;
        if (searchParams.has('error_description')) {
            const name = searchParams.get('error');
            const description = searchParams.get('error_description');
            if (typeof newrelic?.noticeError === 'function') {
                newrelic.noticeError(new Error(`[${name}] ${description}`));
            }
        }
        // TODO: Support federated sign outs? We currently only load this library for new sessions.
        this.hasCode = searchParams.has('code');
        if (this.hasCode) {
            this.setupAuth();
        }
        this.hasInitialized = (async () => {
            try {
                await this.initializeUserPools();
            }
            catch (err) {
                // eslint-disable-next-line no-console
                console.error('[mygrano-auth] init error', err);
                if (newrelic && typeof newrelic.noticeError === 'function') {
                    newrelic.noticeError(err);
                }
            }
            return true;
        })();
    }
    /** Set auth tokens to either cookies or local storage. */
    async setCommonAuthTokens() {
        const tokens = this.getStoredTokens(this.tokenStorage);
        if (this.serviceCognitoConfig.use_shared_auth) {
            await this.createFetchRequest('PUT', tokens);
        }
        else
            this.setLocalStorageTokens(tokens);
    }
    /** Set common session to token storage and determine active user pool. */
    async getCommonSession() {
        const tokens = this.serviceCognitoConfig.use_shared_auth
            ? await this.getCommonAuthTokens()
            : this.getStoredTokens();
        const tokenEntries = Object.entries(tokens);
        if (tokenEntries.length === 0)
            return;
        for (const [key, value] of tokenEntries) {
            this.tokenStorage.setItem(key, value);
        }
        this.discoverActiveUserPool();
    }
    /** Get current Cognito user from active user pool. */
    getCurrentUser() {
        if (!this.activeUserPool)
            return;
        const user = this.activeUserPool.getCurrentUser();
        if (!user)
            return;
        const promisifiedUser = promisifyAll(user, [
            'getSession',
            'getUserAttributes',
            'updateAttributes',
            'changePassword',
        ]);
        // Make CognitoUser.cacheTokens to update cookies when user session gets refreshed
        const cacheTokens = promisifiedUser.cacheTokens;
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        const myGranoAuth = this;
        promisifiedUser.cacheTokens = function (...args) {
            const oldTokens = this.storage.getItems();
            cacheTokens.apply(this, args);
            const newTokens = this.storage.getItems();
            if (Object.keys(oldTokens).length !== Object.keys(newTokens).length ||
                !Object.entries(oldTokens).every(([key, val]) => val === newTokens[key])) {
                void myGranoAuth.setCommonAuthTokens();
            }
        };
        return promisifiedUser;
    }
    /**
     * Authenticate with username and password.
     * @param username Username
     * @param password Password
     * @returns Logged in user data
     */
    async login(username, password) {
        for (const userPool of this.userPools) {
            try {
                const cognitoData = await this.loginToUserPool(username, password, userPool);
                if (cognitoData)
                    return cognitoData;
            }
            catch (err) {
                if (err instanceof Error && err.message === 'User does not exist.')
                    continue; // Try different user pool.
                throw err;
            }
        }
        // All fallbacks tried, but user not found.
        throw new UserDoesNotExistError('User does not exist.');
    }
    /**
     * @see getFederatedSession
     * @deprecated Use .getFederatedSession() instead.
     */
    async parseTokenFromURL() {
        return this.getFederatedSession();
    }
    /**
     * Persists auth tokens for the current federated login session, and returns the session.
     * @returns the current cognito session
     */
    async getFederatedSession() {
        if (!this.cognitoAuth)
            return;
        // Make sure initialization is complete.
        await this.hasInitialized;
        if (!this.currentSession) {
            // Handling the code query parameter is done by @aws-amplify/auth when using responseType 'code'.
            this.currentSession = await this.getCurrentFederatedSession();
        }
        return this.currentSession;
    }
    /**
     * Change password for current user.
     * @param oldPassword Old password
     * @param newPassword New password
     * @returns True if update was successful or Cognito user session
     */
    async changePassword(oldPassword, newPassword) {
        const user = this.getCurrentUser();
        if (user) {
            await user.getSession();
            return user.changePassword(oldPassword, newPassword).then((result) => result === 'SUCCESS');
        }
        if (!this.newUserAttributes)
            throw new NewUserNotAvailableError('New user is not available');
        if (this.newUserAttributes.email_verified !== undefined)
            delete this.newUserAttributes.email_verified;
        if (this.newUserAttributes.phone_number_verified !== undefined)
            delete this.newUserAttributes.phone_number_verified;
        return this.wrapAuthCallbacks((callbacks) => {
            this.newUser.completeNewPasswordChallenge(newPassword, this.newUserAttributes, {
                ...callbacks,
            });
        });
    }
    /** Logout current user. */
    async logout() {
        const user = this.getCurrentUser();
        if (user)
            user.signOut();
        await this.clearCommonAuthTokens();
    }
    /**
     * Get the session of the current user.
     * @returns Cognito user session
     */
    async getSession() {
        const user = this.getCurrentUser();
        if (!user)
            return;
        return user.getSession();
    }
    /**
     * Get Cognito user attributes.
     * @returns Array of Cognito user attributes
     */
    async getUserAttributes() {
        const user = this.getCurrentUser();
        if (!user)
            return [];
        try {
            await user.getSession();
            return user.getUserAttributes();
        }
        catch {
            return [];
        }
    }
    /**
     * Update current user attributes.
     * @param details Key value pair attributes to update
     * @returns True if update was successful
     */
    async updateAttributes(details) {
        const user = this.getCurrentUser();
        await user.getSession();
        const attributes = Object.keys(details).map((key) => ({ Name: key, Value: details[key] }));
        return user.updateAttributes(attributes).then((result) => result === 'SUCCESS');
    }
    /** Verify current user attribute (e.g. email). */
    async verifyAttribute(attribute, verificationCode) {
        const user = this.getCurrentUser();
        await user.getSession();
        return this.wrapAuthCallbacks((callbacks) => user.verifyAttribute(attribute, verificationCode, callbacks)).then((result) => result === 'SUCCESS');
    }
    /** Resend confirmation (for confirmation of registration) to a specific user in the user pool. */
    async resendConfirmationCode(email) {
        const user = new CognitoUser({
            Username: email,
            // TODO: Fallback user pools
            Pool: this.primaryUserPool,
        });
        return new Promise((resolve, reject) => {
            user.resendConfirmationCode((error, result) => {
                if (error)
                    return reject(error);
                resolve(result.CodeDeliveryDetails);
            });
        });
    }
    /**
     * Sends the user attribute verification code for the specified attribute name.
     * @param attribute Verified attribute
     */
    async getAttributeVerificationCode(attribute) {
        const user = this.getCurrentUser();
        await user.getSession();
        return this.wrapAuthCallbacks((callbacks) => user.getAttributeVerificationCode(attribute, callbacks));
    }
    /**
     * Causes a message to be sent to the end user with a confirmation
     * code that is required to change the user's password.
     */
    async requestPasswordReset(email) {
        let args;
        let error;
        for (const userPool of this.userPools) {
            const user = new CognitoUser({
                Username: email,
                Pool: userPool,
            });
            await this.wrapAuthCallbacks((callbacks) => {
                user.forgotPassword({
                    ...callbacks,
                    onSuccess: (successData) => callbacks.onSuccess(successData.CodeDeliveryDetails),
                });
            })
                .then((codeDeliveryDetails) => (args = codeDeliveryDetails))
                .catch((err) => (error = err));
            if (args || (error && error.code !== 'UserNotFoundException'))
                break;
        }
        if (args)
            return args;
        throw error || new Error('requestPasswordReset failed');
    }
    /**
     * Confirm new user password.
     * @returns True if reset was successful
     */
    async resetPassword(email, code, newPassword) {
        let hasSucceeded, error;
        for (const userPool of this.userPools) {
            const user = new CognitoUser({
                Username: email,
                Pool: userPool,
            });
            await this.wrapAuthCallbacks((callbacks) => {
                user.confirmPassword(code, newPassword, callbacks);
            })
                .then(() => (hasSucceeded = true))
                .catch((err) => (error = err));
            if (hasSucceeded || (error && error.code !== 'UserNotFoundException'))
                break;
        }
        if (hasSucceeded)
            return hasSucceeded;
        throw error || new Error('resetPassword failed');
    }
    /**
     * Confirms registration of a user.
     * @param details Email and code for the confirmation
     * @param details.code Code for the confirmation
     * @param details.email Email for the confirmation
     * @returns True if confirmation was successful
     */
    async confirmRegistration(details) {
        const user = new CognitoUser({
            Username: details.email,
            // TODO: Fallback user pools - are they needed for registration?
            Pool: this.primaryUserPool,
        });
        const promisifiedUser = promisifyAll(user, ['confirmRegistration']);
        return promisifiedUser
            .confirmRegistration(details.code, true)
            .then((result) => result === 'SUCCESS');
    }
    setLocalStorageTokens(tokens) {
        for (const [key, value] of Object.entries(tokens)) {
            localStorage.setItem(key, value);
        }
    }
    getStoredTokens(store = localStorage) {
        return Object.fromEntries(Object.entries(store instanceof MemoryStorage ? store.getItems() : store).filter(([key, value]) => /^CognitoIdentityServiceProvider/.exec(key) && typeof value === 'string'));
    }
    clearLocalStorageTokens() {
        const tokens = this.getStoredTokens();
        const tokenKeys = Object.keys(tokens);
        if (tokenKeys.length === 0)
            return;
        for (const key of tokenKeys) {
            localStorage.removeItem(key);
        }
    }
    wrapAuthCallbacks(fn) {
        return new Promise((resolve, reject) => {
            fn({ onSuccess: resolve, onFailure: reject });
        });
    }
    async clearCommonAuthTokens() {
        return this.serviceCognitoConfig.use_shared_auth
            ? this.createFetchRequest('PUT', {})
            : this.clearLocalStorageTokens();
    }
    async createFetchRequest(method, requestBody) {
        const body = requestBody ? JSON.stringify(requestBody) : undefined;
        return fetch(this.authUrl, {
            method,
            body,
            credentials: 'include',
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
                'Cache-Control': 'no-cache',
                Pragma: 'no-cache',
            },
        }).then((res) => {
            if (res.status === 204)
                return;
            return res.json();
        });
    }
    async initializeUserPools() {
        this.userPools = [];
        const cognitoConfig = this.serviceCognitoConfig;
        if (!cognitoConfig)
            throw new MissingCognitoConfigError('Cognito configuration is missing');
        this.activeUserPool = undefined;
        this.addUserPool(cognitoConfig);
        if (cognitoConfig?.fallbacks?.customer)
            this.addUserPool(cognitoConfig.fallbacks.customer);
        if (cognitoConfig?.fallbacks?.admin)
            this.addUserPool(cognitoConfig.fallbacks.admin);
        // First available user pool is set as primary and promisified for signUp.
        this.primaryUserPool = promisifyAll(this.userPools[0], ['signUp']);
        // Check if user has a valid session in one of the user pools
        await this.getCommonSession();
        this.discoverActiveUserPool();
        if (this.hasCode)
            await this.initCognitoAuth(cognitoConfig.federation);
    }
    getUserPoolData(cognitoConfig) {
        return {
            UserPoolId: cognitoConfig.user_pool_id,
            ClientId: cognitoConfig.client_id,
            Storage: this.tokenStorage,
        };
    }
    addUserPool(cognitoConfig) {
        // If either user_pool_id or client_id is missing, don't try to create the user pool.
        if (!cognitoConfig.user_pool_id || !cognitoConfig.client_id)
            return;
        this.userPools.push(new CognitoUserPool(this.getUserPoolData(cognitoConfig)));
    }
    discoverActiveUserPool() {
        if (this.activeUserPool)
            return;
        // Finds the first user pool with an active session and sets it as activeUserpool
        this.activeUserPool = this.userPools.find((userPool) => !!userPool.getCurrentUser());
    }
    async getCommonAuthTokens() {
        return this.createFetchRequest('GET');
    }
    loginToUserPool(username, password, userPool = this.primaryUserPool) {
        const user = new CognitoUser({
            Username: username,
            Pool: userPool,
            Storage: this.tokenStorage,
        });
        const authDetails = new AuthenticationDetails({
            Username: username,
            Password: password,
        });
        this.activeUserPool = userPool;
        return this.wrapAuthCallbacks((callbacks) => {
            user.authenticateUser(authDetails, {
                ...callbacks,
                onSuccess: (cognitoSession) => {
                    this.setCommonAuthTokens().then(() => {
                        const isEmailVerified = cognitoSession?.idToken?.payload?.email_verified === true;
                        callbacks.onSuccess({ isNewUser: false, isEmailVerified, cognitoSession });
                    }, 
                    // TODO: Add a test for this error handling.
                    /* istanbul ignore next */ (err) => callbacks.onFailure(err));
                },
                newPasswordRequired: (userAttributes) => {
                    this.newUser = user;
                    this.newUserAttributes = userAttributes;
                    callbacks.onSuccess({ isNewUser: true });
                },
            });
        });
    }
    setupAuth() {
        this.cognitoAuth = new CognitoAuth({
            hubOptions: {
                authEventHandlers: {
                    signIn: async () => {
                        this.activeUserPool = this.primaryUserPool;
                        await this.setCommonAuthTokens();
                    },
                    signOut: async () => {
                        await this.clearCommonAuthTokens();
                    },
                },
            },
        });
    }
    /**
     * Initializes @aws-amplify/auth that is used for federated sign-ins.
     */
    async initCognitoAuth({ 
    /* eslint-disable @typescript-eslint/naming-convention */
    redirect_sign_in, redirect_sign_out, userpool_domain,
    /* eslint-enable @typescript-eslint/naming-convention */
     }) {
        const authConfig = {
            userPoolId: this.primaryUserPool.getUserPoolId(),
            userPoolWebClientId: this.primaryUserPool.getClientId(),
            storage: this.tokenStorage,
            oauth: {
                domain: userpool_domain,
                scope: ['openid', 'profile', 'email', 'phone', 'aws.cognito.signin.user.admin'],
                redirectSignIn: redirect_sign_in?.[this.locale] ?? redirect_sign_in?.fi,
                redirectSignOut: redirect_sign_out?.[this.locale] ?? redirect_sign_out?.fi,
                responseType: 'code',
            },
        };
        return this.cognitoAuth.configure(authConfig);
    }
    /**
     * Technically, this returns any session registered with @aws-amplify/auth, but we currently only use it
     *  with federated logins.
     * @returns If there's an ongoing federated user session, return it. `undefined` otherwise.
     */
    async getCurrentFederatedSession() {
        try {
            // TODO: Simplify the whole SSO authentication by utilizing auth events via Hub by
            // allowing us to pass custom handlers from the application code for these events.
            return await this.cognitoAuth.currentSession();
        }
        catch (err) {
            const notLoggedInMsg = 'No current user';
            // Authentication code failed so throw an error.
            if (typeof err === 'string' && this.hasCode)
                throw new Error(err);
            if (err instanceof Error && err.message === notLoggedInMsg)
                return;
            throw err;
        }
    }
    /** Get the URL search parameters. */
    getSearchParams() {
        return new URLSearchParams(location.search);
    }
}
